import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle
}: FAQItemProps) {
  return <div className="border-b border-white/10" data-id="element-88">
      <button onClick={onToggle} className="w-full py-5 flex items-center justify-between text-left group" data-id="element-89">
        <span className="font-inter font-semibold text-white text-lg pr-4 group-hover:text-orange-accent transition-colors" data-id="element-90">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0" data-id="element-91">
          <ChevronDownIcon className="w-5 h-5 text-orange-accent" data-id="element-92" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-93">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94]
      }} className="overflow-hidden" data-id="element-94">
            <p className="font-inter text-concrete leading-relaxed pb-5" data-id="element-95">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: "I'm not handy — can I really build this?",
    answer: 'If you can follow instructions and use basic hand tools, you can build this. The guide includes step-by-step photos, exact measurements, and a complete materials list with SKU numbers. Most buyers complete their vault in a single weekend. No special skills required — just patience and attention to detail.'
  }, {
    question: 'Will this work in my rental / apartment / condo?',
    answer: "The primary method requires access to floor joists or a concrete slab, which typically means you need to own your home. However, Bonus #2 includes alternative methods for renters that don't require permanent structural modifications. These are less robust but still far superior to a visible safe."
  }, {
    question: 'Is this legal?',
    answer: "Building concealed storage in your own home is legal in all 50 states. You're not hiding anything from authorities — you're hiding it from criminals. There are no permits required for the construction methods in this guide. Always check local building codes if you're concerned, but this falls under standard home improvement."
  }, {
    question: 'What if I need to access my firearms quickly?',
    answer: "The guide includes rapid-access configurations for home defense scenarios. You can design your vault with a quick-release mechanism that allows access in under 10 seconds while remaining completely invisible to anyone who doesn't know exactly where to look."
  }, {
    question: 'How is this different from a floor safe?',
    answer: "Floor safes are visible, bolted-in metal boxes that scream 'valuables here.' Our method integrates storage into the actual structure of your home — it becomes part of the floor or wall itself. There's nothing to see, nothing to pry, nothing to grind through. It's not a safe hidden in your floor. It IS your floor."
  }, {
    question: 'What about fire protection?',
    answer: "The guide includes a fire-rating section that shows you how to create a 2,000°F heat shield using inexpensive fire-rated drywall. This actually outperforms most 'fire-rated' retail safes, which are often just marketing claims that fail in real house fire conditions."
  }, {
    question: "Can I get a refund if it doesn't work for my situation?",
    answer: "Absolutely. You have 60 days to review the complete guide. If you decide it won't work for your home, your skill level, or any other reason — just email us and we'll refund every penny. No questions, no hassle, no hard feelings."
  }];
  return <section ref={ref} className="w-full bg-dark-bg py-20 md:py-28" data-id="element-96">
      <div className="max-w-3xl mx-auto px-6" data-id="element-97">
        <motion.h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-white text-center mb-4 tracking-wide" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} data-id="element-98">
          QUESTIONS? <span className="text-orange-accent" data-id="element-99">ANSWERS.</span>
        </motion.h2>

        <motion.p className="font-inter text-concrete text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-100">
          Everything you need to know before you decide
        </motion.p>

        <motion.div className="bg-dark-card border border-white/5 p-6 md:p-8" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-101">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-102" />)}
        </motion.div>
      </div>
    </section>;
}